import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Label, toast, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils';
import {
    POS_MORE_ACTIONS,
    POS_PRIMARY_ACTIONS,
    type PosAction,
} from '../../sellerMeta';

/**
 * The top bar's operations: the most common ones as tooltip'd icon buttons,
 * the rest behind a "More" menu (progressive disclosure). All are CTA stubs
 * until their modals land: pressing one toasts "coming soon".
 */
export const SellerTopBarActions: React.FC = () => {
    const { t } = useTranslation('pages');

    const onComingSoon = (label: string) =>
        toast.info(label, { description: t('seller.comingSoon') });

    const renderActionButton = (action: PosAction) => {
        const label = t(action.labelKey);
        return (
            <Tooltip key={action.key} delay={0}>
                <Button
                    aria-label={label}
                    {...getNodeTestIdProp(`seller-action-${action.key}`)}
                    isIconOnly
                    onPress={() => onComingSoon(label)}
                    variant="ghost"
                >
                    <Icon className="size-4" icon={action.icon} />
                </Button>
                <Tooltip.Content>{label}</Tooltip.Content>
            </Tooltip>
        );
    };

    return (
        <div className="flex items-center gap-1">
            {POS_PRIMARY_ACTIONS.map(renderActionButton)}

            <Dropdown>
                <Button
                    aria-label={t('seller.more')}
                    {...getNodeTestIdProp('seller-action-more')}
                    isIconOnly
                    variant="ghost"
                >
                    <Icon className="size-4" icon="solar:menu-dots-bold" />
                </Button>
                <Dropdown.Popover>
                    <Dropdown.Menu
                        aria-label={t('seller.more')}
                        onAction={(key) =>
                            onComingSoon(t(`seller.actions.${key}`))
                        }
                    >
                        {POS_MORE_ACTIONS.map((action) => (
                            <Dropdown.Item
                                id={action.key}
                                key={action.key}
                                textValue={t(action.labelKey)}
                            >
                                <Icon
                                    className="size-4 text-muted"
                                    icon={action.icon}
                                />
                                <Label>{t(action.labelKey)}</Label>
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown.Popover>
            </Dropdown>
        </div>
    );
};
